// app/core/navigation/navigation.service.ts

import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Navigation, OrganizationAwareFuseNavigationItem } from 'app/core/navigation/navigation.types';
import { Observable, ReplaySubject, tap } from 'rxjs';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { getOrganizationData } from 'app/core/organization/organization-utils';

@Injectable({providedIn: 'root'})
export class NavigationService {
    private _httpClient = inject(HttpClient);
    private _fuseNavigationService = inject(FuseNavigationService);
    private _navigation: ReplaySubject<Navigation> = new ReplaySubject<Navigation>(1);

    // Navigation store keys
    private _navigationKeys = {
        default: 'default-navigation',
        compact: 'compact-navigation',
        futuristic: 'futuristic-navigation',
        horizontal: 'horizontal-navigation'
    };

    /**
     * Constructor
     */
    constructor() {}

    /**
     * Getter for navigation
     */
    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }

    /**
     * Get all navigation data
     */
    get(): Observable<Navigation> {
        return this._httpClient.get<Navigation>('api/common/navigation').pipe(
            tap((navigation) => {
                // Apply organization filters
                this.processNavigationItems(navigation);
                
                // Store navigation data in Fuse Navigation Service 
                for (const key in navigation) {
                    if (navigation.hasOwnProperty(key) && this._navigationKeys[key]) {
                        this._fuseNavigationService.storeNavigation(
                            this._navigationKeys[key], 
                            navigation[key]
                        );
                    }
                }
                
                // Emit the processed navigation
                this._navigation.next(navigation);
            }),
        );
    }

    /**
     * Process all navigation items to apply organization-based visibility
     */
    private processNavigationItems(navigation: Navigation): void {
        // Process each navigation section
        for (const section in navigation) {
            if (navigation.hasOwnProperty(section)) {
                this.applyOrganizationFilters(navigation[section]);
            }
        }
    }
    
    /**
     * Apply organization filters to navigation items by setting their hidden property
     */
    private applyOrganizationFilters(items: OrganizationAwareFuseNavigationItem[]): void {
        if (!items || !Array.isArray(items)) {
            return;
        }
        
        // Get organization data once
        const orgData = getOrganizationData();

        // Process each item
        items.forEach(item => {
            // Save original hidden function if it exists
            const originalHiddenFn = item.hidden;
            
            // Create new hidden function that incorporates organization filters
            item.hidden = (navItem) => {
                // Check original hidden function first if it exists
                if (originalHiddenFn && originalHiddenFn(navItem)) {
                    return true;
                }
                
                // Apply organization-specific visibility logic
                return this.shouldHideForOrganization(navItem as OrganizationAwareFuseNavigationItem, orgData);
            };
            
            // Process children recursively
            if (item.children && item.children.length > 0) {
                this.applyOrganizationFilters(item.children as OrganizationAwareFuseNavigationItem[]);
            }
        });
    }
    
    /**
     * Determine if an item should be hidden based on organization data
     */
    private shouldHideForOrganization(item: OrganizationAwareFuseNavigationItem, orgData: any): boolean {
        // Get organization properties
        const orgName = orgData.name || '';
        const orgType = orgData.type || '';
        const features = orgData.features || [];
        
        // Check organization type restrictions
        if (item.forOrganizationTypes && item.forOrganizationTypes.length > 0) {
            if (!item.forOrganizationTypes.includes(orgType)) {
                return true; // Hide the item
            }
        }
        
        // Check for organization name restrictions
        if (item.forOrganizationNames && item.forOrganizationNames.length > 0) {
            if (!item.forOrganizationNames.includes(orgName)) {
                return true; // Hide the item
            }
        }
        
        // Check if the item requires specific features
        if (item.requiredFeatures && item.requiredFeatures.length > 0) {
            const hasAllRequiredFeatures = item.requiredFeatures.every(feature => 
                features.includes(feature)
            );
            
            if (!hasAllRequiredFeatures) {
                return true; // Hide the item
            }
        }
        
        // Apply custom visibility function if defined
        if (item.visibilityCheck && typeof item.visibilityCheck === 'function') {
            try {
                const isVisible = item.visibilityCheck({
                    name: orgName,
                    type: orgType,
                    features: features
                });
                
                if (!isVisible) {
                    return true; // Hide the item
                }
            } catch (error) {
                console.error('Error in visibility check function', error);
                return true; // Hide on error
            }
        }
        
        // Special case for BAISTORE
        if (orgName === 'BAISTORE' && item.showForBaistore === true) {
            return false; // Don't hide
        }
        
        // Default case - not hidden
        return false;
    }
    
    /**
     * Refresh navigation with the latest organization data
     * Call this after changing organization data in localStorage
     */
    refreshNavigation(): void {
        // Get current navigation from Fuse Navigation Service
        const navigation = {
            default: this._fuseNavigationService.getNavigation(this._navigationKeys.default),
            compact: this._fuseNavigationService.getNavigation(this._navigationKeys.compact),
            futuristic: this._fuseNavigationService.getNavigation(this._navigationKeys.futuristic),
            horizontal: this._fuseNavigationService.getNavigation(this._navigationKeys.horizontal)
        };
        
        // Re-apply organization filters
        this.processNavigationItems(navigation);
        
        // Store back to Fuse Navigation Service
        for (const key in navigation) {
            if (navigation.hasOwnProperty(key) && this._navigationKeys[key]) {
                this._fuseNavigationService.storeNavigation(
                    this._navigationKeys[key], 
                    navigation[key]
                );
            }
        }
        
        // Emit the updated navigation
        this._navigation.next(navigation);
    }
}